import React, { useState, useCallback, useMemo } from "react";
import { IncCheckbox, IncSmartText, IncFaIcon, IncRadioButton } from "@inception/ui";
import { PopperOption } from "./types";

interface UseTreeSelectionProps<T> {
  options: Array<PopperOption<T>>;
  multi: boolean;
  parentAsHeader?: boolean;
}

interface UseTreeSelectionReturn<T> {
  expandedParents: Set<T>;
  setExpandedParents: React.Dispatch<React.SetStateAction<Set<T>>>;
  toggleExpanded: (value: T) => void;
  getAllEnabledLeafValues: (opts: Array<PopperOption<T>>) => T[];
  areAllChildrenSelected: (option: PopperOption<T>, currentDraft: T[]) => boolean;
  areSomeChildrenSelected: (option: PopperOption<T>, currentDraft: T[]) => boolean;
  findParentOfChild: (childValue: T, opts?: Array<PopperOption<T>>) => PopperOption<T> | null;
  toggleDraft: (
    value: T,
    option: PopperOption<T>,
    draft: T[],
    setDraft: React.Dispatch<React.SetStateAction<T[]>>
  ) => void;
  renderRow: (
    o: PopperOption<T>,
    level: number,
    draft: T[],
    setDraft: React.Dispatch<React.SetStateAction<T[]>>
  ) => React.ReactNode;
}

export function useTreeSelection<T = string>({
  options,
  multi,
  parentAsHeader = false
}: UseTreeSelectionProps<T>): UseTreeSelectionReturn<T> {
  // Track which parent options are expanded
  const [expandedParents, setExpandedParents] = useState<Set<T>>(new Set());

  // Toggle expanded state for parent options
  const toggleExpanded = useCallback((value: T) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  }, []);

  // Helper function to get all enabled leaf values (children only, not parents)
  const getAllEnabledLeafValues = useCallback((opts: Array<PopperOption<T>>): T[] => {
    const values: T[] = [];
    opts.forEach(opt => {
      if (!opt.disabled) {
        if (opt.children && opt.children.length > 0) {
          // Has children, recurse into them
          values.push(...getAllEnabledLeafValues(opt.children));
        } else {
          // Leaf node, add its value
          values.push(opt.value);
        }
      }
    });
    return values;
  }, []);

  // Check if all children are selected
  const areAllChildrenSelected = useCallback((option: PopperOption<T>, currentDraft: T[]) => {
    if (!option.children || option.children.length === 0) {
      return false;
    }
    const enabledChildren = option.children.filter(c => !c.disabled);
    if (enabledChildren.length === 0) {
      return false;
    }
    return enabledChildren.every(child => currentDraft.includes(child.value));
  }, []);

  // Check if some (but not all) children are selected
  const areSomeChildrenSelected = useCallback(
    (option: PopperOption<T>, currentDraft: T[]) => {
      if (!option.children || option.children.length === 0) {
        return false;
      }
      const enabledChildren = option.children.filter(c => !c.disabled);
      return (
        enabledChildren.some(child => currentDraft.includes(child.value)) &&
        !areAllChildrenSelected(option, currentDraft)
      );
    },
    [areAllChildrenSelected]
  );

  // Helper to find parent of a child option
  const findParentOfChild = useCallback(
    (childValue: T, opts: Array<PopperOption<T>> = options): PopperOption<T> | null => {
      for (const opt of opts) {
        if (opt.children && opt.children.length > 0) {
          if (opt.children.some(c => c.value === childValue)) {
            return opt;
          }
          const found = findParentOfChild(childValue, opt.children);
          if (found) {
            return found;
          }
        }
      }
      return null;
    },
    [options]
  );

  // Toggle draft with parent/child logic
  const toggleDraft = useCallback(
    (value: T, option: PopperOption<T>, draft: T[], setDraft: React.Dispatch<React.SetStateAction<T[]>>) => {
      setDraft(prev => {
        const isCurrentlySelected = prev.includes(value);
        const hasChildren = option.children && option.children.length > 0;

        // When parentAsHeader is true, parent options are not selectable
        if (parentAsHeader && hasChildren) {
          return prev; // Group headers are not selectable
        }

        if (multi) {
          // Multi-select mode logic
          if (hasChildren && option.children) {
            // Parent option clicked
            const enabledChildren = option.children.filter(c => !c.disabled);
            const childValues = enabledChildren.map(c => c.value);
            const allChildrenSelected = enabledChildren.every(c => prev.includes(c.value));

            if (allChildrenSelected) {
              // Deselect all children
              return prev.filter(v => !childValues.includes(v));
            } else {
              // Select all enabled children
              const newValues = [...prev];
              childValues.forEach(cv => {
                if (!newValues.includes(cv)) {
                  newValues.push(cv);
                }
              });
              return newValues;
            }
          } else {
            // Child option clicked
            if (isCurrentlySelected) {
              return prev.filter(v => v !== value);
            } else {
              return [...prev, value];
            }
          }
        } else {
          // Single-select mode logic
          if (hasChildren && option.children && !parentAsHeader) {
            // Parent option clicked - select all its children, deselect everything else
            const enabledChildren = option.children.filter(c => !c.disabled);
            const childValues = enabledChildren.map(c => c.value);
            const allChildrenSelected = enabledChildren.every(c => prev.includes(c.value));

            if (allChildrenSelected && prev.length === childValues.length) {
              // If this parent's children are the only things selected, deselect all
              return [];
            } else {
              // Select all children of this parent, clear everything else
              return childValues;
            }
          } else {
            // Child option clicked - single select behavior
            if (parentAsHeader) {
              // When parentAsHeader is true, single select means only one value at a time
              // When a radio button is clicked, it should always select that value (replace previous)
              return [value];
            } else {
              // Standard single select logic
              const parent = findParentOfChild(value, options);

              if (!parent || !parent.children) {
                // No parent found, treat as single select
                return isCurrentlySelected ? [] : [value];
              }

              // Check if we have selections from a different parent
              const siblingValues = parent.children.map(c => c.value);
              const hasSelectionsFromDifferentParent = prev.some(v => !siblingValues.includes(v));

              if (hasSelectionsFromDifferentParent) {
                // Clear all previous selections and select only this child
                return [value];
              } else {
                // Toggle within the same parent
                if (isCurrentlySelected) {
                  return prev.filter(v => v !== value);
                } else {
                  return [...prev, value];
                }
              }
            }
          }
        }
      });
    },
    [multi, parentAsHeader, findParentOfChild, options]
  );

  // Generate a unique name for radio button groups when multi is false (single-select mode)
  const radioGroupName = useMemo(() => (!multi ? `inc-ddv2-radio-group-${Date.now()}` : undefined), [multi]);

  // Render row with nested support
  const renderRow = useCallback(
    (
      o: PopperOption<T>,
      level: number,
      draft: T[],
      setDraft: React.Dispatch<React.SetStateAction<T[]>>
    ): React.ReactNode => {
      const { disabled, children: childOptions } = o;
      const hasChildren = childOptions && childOptions.length > 0;
      const isExpanded = expandedParents.has(o.value);
      const isGroupHeader = parentAsHeader && hasChildren && level === 0;

      // For parent options, check children selection state
      const allChildrenSelected = hasChildren && areAllChildrenSelected(o, draft);
      const someChildrenSelected = hasChildren && areSomeChildrenSelected(o, draft);
      const checked = hasChildren ? allChildrenSelected : draft.includes(o.value);

      const handleRowClick = () => {
        if (disabled) {
          return;
        }

        // When parentAsHeader is true, group headers are not clickable
        if (isGroupHeader) {
          return;
        }

        if (multi) {
          // Multi mode: only handle clicks for children, parents use checkbox/radio
          if (!hasChildren) {
            toggleDraft(o.value, o, draft, setDraft);
          }
        } else {
          // Single mode: handle clicks for both parents and children
          toggleDraft(o.value, o, draft, setDraft);
          // Don't close dropdown yet to allow selecting children from same parent
        }
      };

      return (
        <React.Fragment key={String(o.value)}>
          <div
            className={`inc-ddv2-row inc-flex-row inc-flex-center-vertical ${level > 0 ? "inc-ddv2-row-nested" : ""} ${isGroupHeader ? "inc-ddv2-group-header" : ""}`}
            data-disabled={disabled}
            data-level={level}
            data-selected={checked || draft.includes(o.value)}
            onClick={handleRowClick}
            style={{
              paddingLeft: level > 0 ? `${level * 20 + 4}px` : undefined,
              paddingRight: level > 0 ? "12px" : undefined,
              cursor: isGroupHeader ? "default" : disabled ? "not-allowed" : "pointer"
            }}
          >
            {!isGroupHeader &&
              (!multi ? (
                <IncRadioButton
                  checked={checked}
                  disabled={disabled}
                  name={radioGroupName}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!disabled) {
                      toggleDraft(o.value, o, draft, setDraft);
                    }
                  }}
                />
              ) : (
                <IncCheckbox
                  checked={checked}
                  disabled={disabled}
                  indeterminate={someChildrenSelected}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    if (!disabled) {
                      toggleDraft(o.value, o, draft, setDraft);
                    }
                  }}
                  skipChangeOnLabelClick
                />
              ))}

            {o.icon && <span className="inc-ddv2-icon">{o.icon}</span>}

            <div
              className="inc-flex-row inc-flex-center-vertical flex-gap-4"
              style={{ flex: "1 1 auto" }}
            >
              <IncSmartText
                className="inc-ddv2-text"
                numLines={1}
                style={{ flex: "0 1 auto" }}
                text={o.label}
              />

              {hasChildren && <span className="inc-ddv2-child-count">({childOptions.length})</span>}
            </div>

            {hasChildren && (
              <button
                aria-label={isExpanded ? "Collapse" : "Expand"}
                className="inc-ddv2-expand-btn"
                onClick={e => {
                  e.stopPropagation();
                  toggleExpanded(o.value);
                }}
                type="button"
              >
                <IncFaIcon iconName={isExpanded ? "chevron-up" : "chevron-down"} />
              </button>
            )}
          </div>

          {hasChildren && isExpanded && (
            <div className="inc-ddv2-children">
              {childOptions.map(child => renderRow(child, level + 1, draft, setDraft))}
            </div>
          )}
        </React.Fragment>
      );
    },
    [
      expandedParents,
      areAllChildrenSelected,
      areSomeChildrenSelected,
      multi,
      parentAsHeader,
      toggleDraft,
      toggleExpanded,
      radioGroupName
    ]
  );

  return {
    expandedParents,
    setExpandedParents,
    toggleExpanded,
    getAllEnabledLeafValues,
    areAllChildrenSelected,
    areSomeChildrenSelected,
    findParentOfChild,
    toggleDraft,
    renderRow
  };
}
